import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, FormControl, Input, InputLabel, TextField, withStyles } from '@material-ui/core';
import styles from './KpiFilterPanel.styles';
import classNames from 'classnames';
import { format, subMonths } from 'date-fns';
import Loading from '../Loading';
import { Autocomplete } from '../Autocomplete';
import { useEffect } from 'react';
import { getAgentOptions, getClientInvisionIQKpiFilters } from '../../services';
import { cloneDeep } from 'lodash';

const KpiFilterPanel = ({ classes, onRefresh, loading, clientId }) => {
  // #region STATE
  const [formValues, setFormValues] = useState({
    startDate: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    state: null,
    county: null,
    importHeaderId: null,
    agentMasterId: null,
  });

  const [kpiFilterOptionsLoading, setKpiFilterOptionsLoading] = useState(false);
  const [kpiFilterOptions, setKpiFilterOptions] = useState(null);

  const [selectedState, setSelectedState] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedImportHeader, setSelectedImportHeader] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  // #endregion

  // #region FUNCTIONS
  const getSelectOptions = async () => {
    try {
      setKpiFilterOptionsLoading(true);

      const filters = await getClientInvisionIQKpiFilters({
        clientId,
        stateId: selectedState?.value,
      });

      filters.agents = filters.agents
        ?.map(agent => ({
          value: agent.agentMasterId,
          label: `${agent.agentName}${!!agent.agentCode ? ` (${agent.agentCode})` : ''}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      filters.states = filters.states
        ?.map(state => ({
          value: state.stateId,
          label: `${state.name} (${state.abv})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      filters.counties = filters.counties
        ?.map(county => ({ value: county.countyId, label: county.name }))
        .sort((a, b) => a.label.localeCompare(b.label));

      filters.importHeaders = filters.importHeaders
        ?.map(ih => ({
          value: ih.importHeaderId,
          label: `${ih.fileName} ${!!ih.createdOn ? `(${format(new Date(ih.createdOn), 'MM/dd/yyyy')})` : ''}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setKpiFilterOptions(filters);
    } catch (error) {
      //
      console.log(error);
    } finally {
      setKpiFilterOptionsLoading(false);
    }
  };

  const handleInputUpdate = field => evt => {
    setFormValues({ ...formValues, [field]: evt.target.value });
  };

  const handleSelectUpdate = field => opt => {
    let formUpdates = { ...formValues, [field]: opt?.value };

    switch (field) {
      case 'state':
        setSelectedState(opt);
        formUpdates.county = null;
        break;
      case 'county':
        setSelectedCounty(opt);
        break;
      case 'importHeaderId':
        setSelectedImportHeader(opt);
        break;
      case 'agentMasterId':
        setSelectedAgent(opt);
        break;
      default:
        break;
    }

    setFormValues(formUpdates);
  };

  // #endregion

  // #region EFFECTS
  useEffect(() => {
    getSelectOptions();
  }, [clientId, selectedState]);
  // #endregion

  return (
    <>
      <div className={classes.selectInput}>
        <Autocomplete
          value={selectedState}
          loading={kpiFilterOptionsLoading}
          options={kpiFilterOptions?.states}
          placeholder="State"
          onChange={handleSelectUpdate('state')}
          isClearable
        />
      </div>

      <div className={classes.selectInput}>
        <Autocomplete
          value={selectedCounty}
          loading={kpiFilterOptionsLoading}
          options={kpiFilterOptions?.counties}
          placeholder={`County${!selectedState ? ` (select state first)` : ''}`}
          onChange={handleSelectUpdate('county')}
          isClearable
        />
      </div>

      <div className={classes.selectInput}>
        <Autocomplete
          value={selectedImportHeader}
          loading={kpiFilterOptionsLoading}
          options={kpiFilterOptions?.importHeaders}
          placeholder="Import Header"
          onChange={handleSelectUpdate('importHeaderId')}
          isClearable
        />
      </div>

      <div className={classes.selectInput}>
        <Autocomplete
          value={selectedAgent}
          loading={kpiFilterOptionsLoading}
          options={kpiFilterOptions?.agents}
          placeholder="Agent"
          onChange={handleSelectUpdate('agentMasterId')}
          isClearable
        />
      </div>

      <FormControl variant="standard" className={classes.formControl}>
        <InputLabel shrink htmlFor="startDate">
          Start Date
        </InputLabel>
        <TextField
          type="date"
          name="startDate"
          variant="outlined"
          value={formValues.startDate}
          onChange={handleInputUpdate('startDate')}
          className={classes.input}
        />
      </FormControl>

      <FormControl variant="standard" className={classNames(classes.formControl)}>
        <InputLabel shrink htmlFor="endDate">
          End Date
        </InputLabel>
        <TextField
          type="date"
          name="endDate"
          variant="outlined"
          value={formValues.endDate}
          onChange={handleInputUpdate('endDate')}
          className={classes.input}
        />
      </FormControl>

      <FormControl variant="standard" className={classes.formControl}>
        <InputLabel shrink htmlFor="customerReferenceNumber">
          Customer Reference Number
        </InputLabel>
        <TextField
          type="text"
          name="customerReferenceNumber"
          variant="outlined"
          value={formValues.customerReferenceNumber}
          onChange={handleInputUpdate('customerReferenceNumber')}
          className={classes.input}
        />
      </FormControl>

      <Button
        //
        className={classNames(classes.submitButton)}
        onClick={() => onRefresh(formValues)}
        variant="contained"
        disabled={loading}
      >
        <span
          style={{
            visibility: loading ? 'hidden' : 'visible',
          }}
        >
          Refresh Results
        </span>

        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Loading size={20} />
          </div>
        )}
      </Button>
    </>
  );
};

KpiFilterPanel.propTypes = {
  classes: PropTypes.object,
};

export default withStyles(styles)(KpiFilterPanel);
